import { getFunctions, httpsCallable } from "firebase/functions";

import { firebaseApp } from "./firebase";
import {
  buildCentralDashboardDailyExport,
  normalizeCentralDashboardSummary,
  rankCentralDashboardWork,
} from "./central-dashboard-contract";

export { buildCentralDashboardDailyExport, getCentralDeliveryProviderPresentation, normalizeCentralDashboardSummary, rankCentralDashboardWork } from "./central-dashboard-contract";

export type CentralDashboardPriority = "Critical" | "High" | "Normal";

export type CentralDashboardDeliveryProvider = {
  canSend: boolean;
  channel: "email" | "sms";
  configured: boolean;
  label: "Email" | "SMS";
  senderSource: "default" | "tenant" | "none";
  state: "configured" | "unavailable" | "degraded" | "canary_failed";
  status: string;
  tone: "ready" | "attention" | "setup_required";
  updatedAt: string | null;
};

export type CentralDashboardAttention = {
  assignedTo: string;
  caseId: string;
  caseName: string;
  detail: string;
  href: string;
  id: string;
  receivedAt?: string | null;
  updatedAt?: string | null;
  dueAt?: string | null;
  version?: string;
  linkedCount: number;
  priority: CentralDashboardPriority;
  title: string;
  type: "callback" | "checklist" | "case" | "signature";
};

export type CentralDashboardSummary = {
  attention: CentralDashboardAttention[];
  deliveryProviders: CentralDashboardDeliveryProvider[];
  counts: {
    casesNeedingReview: number;
    firstCalls: number;
    formsWaitingForSignature: number;
    missingDocuments: number;
    openCases: number;
    paymentsPending: number;
    servicesThisWeek: number;
    signaturePackets: number;
  };
  financial: {
    revenueAtRiskCents: number;
    statementsInReviewCents: number;
    unsignedBalancesCents: number;
  } | null;
  familyFollowUps: Array<{
    canRemind: boolean;
    caseId: string;
    caseName: string;
    completedSections: number;
    familyName: string;
    href: string;
    nextSectionLabel: string | null;
    notificationStatus: "accepted" | "failed" | "not_needed" | "not_sent" | "pending";
    progress: "approved" | "complete" | "open" | "ready_for_review" | "saved";
    reviewedSubmissionRevision: number;
    stage: "intake" | "personal" | "services";
    stageLabel: string;
    totalSections: number;
  }>;
  nextActionQueues: Array<{
    assignedTo: string;
    count: number;
    description: string;
    href: string;
    id: "callbacks" | "documents" | "signatures" | "reviews";
    linkedCount: number;
    priority: CentralDashboardPriority;
    title: string;
  }>;
  priorityCases: Array<{
    balance: string;
    director: string;
    due: string;
    family: string;
    id: string;
    lovedOne: string;
    next: string;
    paperwork: string;
    priority: CentralDashboardPriority;
    signatures: string;
    status: string;
  }>;
  recentActivity: Array<{
    caseId: string;
    id: string;
    label: string;
    time: string | null;
  }>;
  reportLinks: Array<{
    description: string;
    href: string;
    id: string;
    label: string;
    permission: "operations" | "financial";
  }>;
};

const functions = getFunctions(firebaseApp, "us-east1");

export async function getCentralDashboardSummary() {
  const callable = httpsCallable<void, CentralDashboardSummary>(functions, "getCentralDashboardSummary");
  return normalizeCentralDashboardSummary((await callable()).data);
}

export async function dismissCentralDashboardActions(items: Array<{ id: string; version: string }>) {
  const callable = httpsCallable<{ items: Array<{ id: string; version: string }> }, { dismissed: number }>(functions, "dismissCentralDashboardActions");
  return (await callable({ items })).data;
}

export function downloadCentralDashboardDailyExport(summary: CentralDashboardSummary, now = new Date()) {
  const result = buildCentralDashboardDailyExport(summary, now);
  if (typeof document === "undefined") return result;
  const url = URL.createObjectURL(new Blob([result.contents], { type: "text/csv;charset=utf-8" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = result.fileName;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
  return result;
}

export function getCentralDashboardErrorMessage(error: unknown) {
  const code = typeof error === "object" && error && "code" in error ? String(error.code) : "";
  if (code.includes("unauthenticated")) {
    return "Your Central session has ended. Sign in again to load the dashboard.";
  }
  if (code.includes("permission-denied")) {
    return "Your account does not have permission to view this dashboard.";
  }
  return "The dashboard could not load. Try again.";
}
